import { envConfig } from './env'
import { PII_FIELDS, sanitizeUrl, scrubTraits } from './personalIdentifierInfo/pIISanitizeUtils'

/**
 * Gainsight PX (https://www.gainsight.com/product-experience/) is a product
 * analytics platform. The integration is intentionally limited to the
 * production deploy so we do not pollute the production dashboard with
 * traffic from local dev / staging / sandbox environments.
 *
 * The tag key is sourced from `envConfig.GAINSIGHT_PX_TAG_KEY` (managed by
 * SRE per environment, see `docs/ENVIRONMENT_VARIABLES.md`).
 *
 * SYN-6649: FE: Add support for Gainsight PX overlay
 */

const GAINSIGHT_PX_SDK_URL = 'https://web-sdk.aptrinsic.com/api/aptrinsic.js'

/**
 * Shape of the global `aptrinsic` queue/stub installed by the official
 * Gainsight PX snippet. Calls made before the SDK script has finished
 * loading are buffered on `q` and flushed by the SDK at startup.
 */
type AptrinsicQueueFn = {
  (...args: unknown[]): void
  q?: unknown[][]
  p?: string
  c?: AptrinsicConfig
}

/**
 * The URL bundle PX hands to `maskUrlFunction` before a tracking event leaves
 * the browser. `url` is undefined when the tracked location is relative — the
 * value lives on `path` instead — so every field has to be handled.
 */
type AptrinsicUrlPayload = {
  url?: string
  path?: string
  title?: string
  referrer?: string
}

/** The subset of the PX boot config we set. */
type AptrinsicConfig = {
  namedParamFilter: string[]
  nameParamFilterType: 'mask' | 'exclude'
  maskUrlFunction: (urlPayload: AptrinsicUrlPayload) => AptrinsicUrlPayload
}

/**
 * `namedParamFilter` only matches top-level param *names*, so a redirect param
 * such as `?next=%2Fmanage%2Fusers%3Femail%3Dada%40kudos.com` is forwarded
 * whole and the nested `email` escapes redaction. `maskUrlFunction` is PX's
 * escape hatch for full control over the tracked URL, so run the same recursive
 * `sanitizeUrl` the other analytics integrations use.
 *
 * PX requires the argument itself to be returned, so mutate in place.
 * https://support.gainsight.com/PX/Administration/General/Exclude_and_Mask_Tracked_Data
 */
const maskGainsightPxUrl = (urlPayload: AptrinsicUrlPayload): AptrinsicUrlPayload => {
  if (urlPayload.url !== undefined) urlPayload.url = sanitizeUrl(urlPayload.url)
  if (urlPayload.path !== undefined) urlPayload.path = sanitizeUrl(urlPayload.path)
  if (urlPayload.referrer !== undefined) {
    urlPayload.referrer = sanitizeUrl(urlPayload.referrer)
  }
  return urlPayload
}

declare global {
  interface Window {
    aptrinsic?: AptrinsicQueueFn
  }
}

export type GainsightPxUser = {
  id: string
  role?: string | null
  createdAt?: string | null
}

export type GainsightPxAccount = {
  id: string | number
  name?: string | null
  licenseType?: string | null
}

const isGainsightPxEnabled = (): boolean => envConfig.GAINSIGHT_PX_TAG_KEY !== ''

let isScriptInjected = false

/**
 * Installs the Gainsight PX tag on the page. Safe to call multiple times — the
 * script is only injected once. No-op outside the production environment or
 * when no tag key has been configured for the current env.
 */
export const loadGainsightPx = (): void => {
  if (!isGainsightPxEnabled()) return
  if (isScriptInjected) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const tagKey = envConfig.GAINSIGHT_PX_TAG_KEY

  // The official Gainsight PX snippet, translated to TypeScript. Sets up a
  // queueing stub at `window.aptrinsic` so any identify/track calls made
  // before the SDK script finishes loading are buffered and replayed once
  // the real SDK is ready.
  const queueFn: AptrinsicQueueFn = function (...args: unknown[]) {
    queueFn.q = queueFn.q || []
    queueFn.q.push(args)
  }
  queueFn.p = tagKey
  // Boot config: exclude PII query params from every URL PX tracks (page views,
  // link clicks, referrals). Param names come from the shared PII registry;
  // `maskUrlFunction` covers the PII those names cannot reach (see above).
  // https://support.gainsight.com/PX/API_for_Developers/01About/Security_Tracking_Filters
  queueFn.c = {
    namedParamFilter: [...PII_FIELDS],
    nameParamFilterType: 'mask',
    maskUrlFunction: maskGainsightPxUrl,
  }
  window.aptrinsic = queueFn

  const script = document.createElement('script')
  script.async = true
  script.src = `${GAINSIGHT_PX_SDK_URL}?a=${tagKey}`

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  isScriptInjected = true
}

/**
 * Associates the current browser session with a known user + account so that
 * subsequent events tracked by the Gainsight PX tag are attributed to them.
 * No-op outside production or before {@link loadGainsightPx} has run.
 *
 * The field names sent over the wire match the Gainsight PX out-of-the-box
 * user / account model:
 * https://support.gainsight.com/PX/Administration/General/User_and_Account_Model
 */
export const identifyGainsightPx = (user: GainsightPxUser, account: GainsightPxAccount): void => {
  if (!isGainsightPxEnabled()) return
  if (typeof window === 'undefined' || typeof window.aptrinsic !== 'function') return

  // Drop any PII-named keys so newly added fields are scrubbed automatically.
  window.aptrinsic(
    'identify',
    scrubTraits({
      id: user.id,
      role: user.role ?? undefined,
      created_at: user.createdAt ?? undefined,
    }),
    scrubTraits({
      id: String(account.id),
      name: account.name ?? undefined,
      license_type: account.licenseType ?? undefined,
    }),
  )
}
